import type { OrganizationChart } from '@the-real-insight/tri-model';

/**
 * Where each company of an organization chart is drawn.
 *
 * Computed, not stored. The chart is about who holds whom, and a position dragged into place would
 * be a second truth that ages the moment a company is added above it. Holders stand above what
 * they hold: a company's row is one below its deepest holder, so a company held through two chains
 * of different length still sits under both.
 *
 * Within a row the companies are ordered by where their holders stand (the mean of their holders'
 * places), which keeps most lines from crossing without a layout library.
 */

export const NODE_WIDTH = 184;
export const NODE_HEIGHT = 48;
const COLUMN_GAP = 32;
const ROW_GAP = 72;

export interface LaidOutCompany {
    id: string;
    x: number;
    y: number;
}

export const layoutOrganizationChart = (chart: OrganizationChart): LaidOutCompany[] => {
    const holdersOf = new Map<string, string[]>();
    chart.shareholdings.forEach((entry) => {
        const holders = holdersOf.get(entry.childId);
        if (holders) holders.push(entry.parentId);
        else holdersOf.set(entry.childId, [entry.parentId]);
    });

    // The chart is free of circles (the model refuses them), so this terminates; the guard is for
    // a stored value written before that rule.
    const depth = new Map<string, number>();
    const depthOf = (id: string, visiting: Set<string> = new Set()): number => {
        const known = depth.get(id);
        if (known !== undefined) return known;
        if (visiting.has(id)) return 0;

        visiting.add(id);
        const holders = holdersOf.get(id) ?? [];
        const value = holders.length === 0 ? 0 : 1 + Math.max(...holders.map((holder) => depthOf(holder, visiting)));
        visiting.delete(id);
        depth.set(id, value);
        return value;
    };

    const rows: string[][] = [];
    chart.companies.forEach((company) => {
        const row = depthOf(company.id);
        (rows[row] ??= []).push(company.id);
    });

    const place = new Map<string, number>();
    rows.forEach((row = [], rowIndex) => {
        if (rowIndex > 0) {
            const centre = (id: string) => {
                const holders = (holdersOf.get(id) ?? []).filter((holder) => place.has(holder));
                return holders.length === 0
                    ? Number.POSITIVE_INFINITY
                    : holders.reduce((sum, holder) => sum + place.get(holder)!, 0) / holders.length;
            };
            // Stable: companies with the same centre keep the order they were added in. The centre
            // is worked out once per company, not on every comparison.
            const centreOf = new Map(row.map((id) => [id, centre(id)]));
            row.sort((a, b) => centreOf.get(a)! - centreOf.get(b)!);
        }
        row.forEach((id, index) => place.set(id, index - (row.length - 1) / 2));
    });

    return rows.flatMap((row = [], rowIndex) =>
        row.map((id) => ({
            id,
            x: place.get(id)! * (NODE_WIDTH + COLUMN_GAP),
            y: rowIndex * (NODE_HEIGHT + ROW_GAP),
        })),
    );
};

type Point = { x: number; y: number };

/** Whether anything in the chart was placed by hand. */
export const isPlacedByHand = (chart: OrganizationChart): boolean =>
    chart.companies.some((company) => !!company.position);

/**
 * Where each company stands on screen.
 *
 * Laid out on its own until the first box is moved. From then on the stored positions count; a
 * company without one (written before positions existed) keeps its computed place.
 */
export const positionsOf = (chart: OrganizationChart): Map<string, Point> => {
    const computed = new Map(layoutOrganizationChart(chart).map((entry) => [entry.id, { x: entry.x, y: entry.y }]));
    if (!isPlacedByHand(chart)) return computed;

    return new Map(
        chart.companies.map((company) => [company.id, company.position ?? computed.get(company.id) ?? { x: 0, y: 0 }]),
    );
};

/** The chart with every company pinned where it stands now — what the first move turns it into. */
export const withPositions = (chart: OrganizationChart, positions: Map<string, Point>): OrganizationChart => ({
    ...chart,
    companies: chart.companies.map((company) => {
        const position = positions.get(company.id);
        return position ? { ...company, position: { x: Math.round(position.x), y: Math.round(position.y) } } : company;
    }),
});

const overlaps = (a: Point, b: Point): boolean =>
    Math.abs(a.x - b.x) < NODE_WIDTH + COLUMN_GAP / 2 && Math.abs(a.y - b.y) < NODE_HEIGHT + ROW_GAP / 2;

/**
 * A free place one row above or below `anchor`, as close to straight above or below it as the boxes
 * already there allow: straight, then one column right, one left, two right, and so on.
 */
export const freeSpotNear = (
    positions: Iterable<Point>,
    anchor: Point,
    direction: 'above' | 'below' | 'beside',
): Point => {
    const taken = Array.from(positions);
    const y =
        direction === 'above'
            ? anchor.y - (NODE_HEIGHT + ROW_GAP)
            : direction === 'below'
              ? anchor.y + NODE_HEIGHT + ROW_GAP
              : anchor.y;
    const step = NODE_WIDTH + COLUMN_GAP;

    for (let offset = direction === 'beside' ? 1 : 0; offset < 200; offset++) {
        const candidates = offset === 0 ? [0] : direction === 'beside' ? [offset] : [offset, -offset];
        for (const column of candidates) {
            const spot = { x: anchor.x + column * step, y };
            if (!taken.some((point) => overlaps(point, spot))) return spot;
        }
    }

    return { x: anchor.x, y };
};
